import type { AmendEdit, AmendResult, AmendSource, PreviewResult } from "../adapters/amend.js";
import type { AccountView, BundleView, RefusedStep, SignedStep } from "../api/types.js";
import { formatMoney } from "../lib/format.js";
import type { BankCard, EditRow, EditView, LineError } from "../ui/bankcard.js";
import { AccountSheet, AmountSheet, dependantsSheet, type DependantsChoice } from "../ui/edit-sheets.js";
import { accountLabel, affectedBy, amountOf, availLine, editKind, offersSource, sourceChoices, whatOf, whoLine } from "./edit-model.js";

/**
 * DCTA-170 — the card's edit state in place (docs/design/v3/03-edit-in-place.md §0.2; F34–F38, F40): one editor per card
 * being edited. Each change goes to the bank (`AmendSource`) on its own; the bank answers a new bundle — drawn in the old
 * card's place (`swap`) — or a refusal (the card stays as it was). Done leaves the edit state with the bank's latest card;
 * Sign is never on the card while it is edited. One request at a time: a second tap while one is in flight does nothing.
 */
export interface EditorDeps {
  readonly source: AmendSource;
  /** The card being edited and its bundle, now (they change with each answer). */
  readonly card: () => BankCard;
  readonly bundle: () => BundleView;
  /** Draws the bank's new bundle as this card's next version, in its place (same handlers, still being edited). */
  readonly swap: (bundle: BundleView) => BankCard;
  /** A sheet over the chat (openSheet with the chat's regions behind it); `close` resolves once it is gone. */
  readonly openSheet: (sheet: HTMLElement, onDismiss: () => void) => { readonly close: () => Promise<void> };
  /** 22w's "Cancel it": the gateway's cancel route for this bundle; the bank's time of it, or undefined when it failed. */
  readonly cancel: (bundle: BundleView) => Promise<{ readonly at: string | undefined } | undefined>;
  /** 409 BUNDLE_NOT_AWAITING: the card's real end (signed elsewhere, cancelled, expired) — the chat reads and draws it. */
  readonly ended: () => void;
  /** Left the edit state: `changed` — the bank's latest card is a new version (Revised); `removed` — its lines above Sign. */
  readonly left: (how: EditLeft) => void;
  readonly onLimits?: (() => void) | undefined;
  /** 150 ms (§0.2): a near-instant answer shows no flash. */
  readonly busyAfterMs?: number;
  /** F39: the customer's accounts, the bank's read (`GET /v1/accounts`) — read once when Edit is tapped; absent or failed: no account control. */
  readonly accounts?: (() => Promise<readonly AccountView[]>) | undefined;
  /**
   * H7 (DCTA-171): a Yes the bank carries stopped this card mid-edit — the edit is refused in the pause's words
   * (`SCAM_CHECK_PAUSED`) and the bank cancelled the cards named in `cancelled`; the chat draws their paused end.
   */
  readonly paused?: ((refused: RefusedStep, cancelled: readonly string[]) => void) | undefined;
}

/** A step removed from the card: its row 11 words, and the bundle it was last in (Undo restores it from there). */
export interface Removal {
  readonly stepId: string;
  readonly what: string;
  readonly from: string;
}

/** How the edit state ended (EditorDeps.left). */
export interface EditLeft {
  readonly changed: boolean;
  readonly removed: readonly Removal[];
  readonly cancelled: boolean;
}

/** A step removed in this edit, and where its row stood on the card as it was when Edit was tapped. */
interface Removed extends Removal {
  readonly index: number;
  /** It went with the step the customer removed (the bank's move, a share): Undo of that step brings it back. */
  readonly along?: true;
}

/** Row 22x (§F.2): the edit did not reach the bank · the card changed under it (another tab). */
export const EDIT_WORDS = {
  unreachable: "Couldn't reach the bank, so nothing changed.",
  stale: "The card changed, so this edit wasn't saved. This is the latest version.",
} as const;

export class CardEditor {
  readonly #d: EditorDeps;
  /** The card as it was when Edit was tapped: its order, its figures (Keep it as it was). */
  readonly #base: BundleView;
  /** Each step's place on that card: rows keep it through every version, removed rows included. */
  readonly #order: ReadonlyMap<string, number>;
  #removed: Removed[] = [];
  #changed = false;
  #inFlight = false;
  #busyShown = false;
  #pending: ReadonlySet<string> = new Set();
  #busyTimer: ReturnType<typeof setTimeout> | undefined;
  #error: LineError | undefined;
  #last: { readonly stepId: string; readonly what: string } | undefined;
  #sheet: { readonly close: () => Promise<void> } | undefined;
  #amountSheet: AmountSheet | undefined;
  #accountSheet: AccountSheet | undefined;
  /** F39: the customer's accounts as read when Edit was tapped (none until the read lands, or when it fails). */
  #accounts: readonly AccountView[] = [];
  #done = false;

  constructor(deps: EditorDeps) {
    this.#d = deps;
    this.#base = deps.bundle();
    this.#order = new Map(this.#base.signing_payload.steps.map((s, i) => [s.step_id, i]));
  }

  get active(): boolean {
    return !this.#done;
  }

  /** Edit was tapped: the card turns into its edit state where it stands; nothing is sent yet. Focus to the hint (§0.2). */
  start(): void {
    this.#render();
    this.#d.card().focus({ kind: "hint" });
    void this.#readAccounts();
  }

  /** F39: one read of the customer's accounts; the rows that could come from another account then show the control. */
  async #readAccounts(): Promise<void> {
    if (this.#d.accounts === undefined) return;
    const accounts = await this.#d.accounts().catch(() => undefined);
    if (accounts === undefined || this.#done) return;
    this.#accounts = accounts;
    this.#render();
  }

  /** The card left the stream's live slot (a new request, its expiry): sheets close, nothing more is sent. */
  dispose(): void {
    this.#done = true;
    if (this.#busyTimer !== undefined) clearTimeout(this.#busyTimer);
    void this.#closeSheet();
  }

  // ---------------------------------------------------------------- the view

  #flags() {
    return this.#d.bundle().firewall?.data_flags ?? [];
  }

  #view(): EditView {
    const steps = this.#d.bundle().signing_payload.steps;
    const flags = this.#flags();
    const rows: { readonly at: number; readonly row: EditRow }[] = [
      ...steps.map((step, i) => {
        const offer = editKind(step);
        return {
          at: this.#order.get(step.step_id) ?? this.#order.size + i,
          row: {
            kind: "step" as const,
            step,
            offer,
            what: whatOf(step, flags),
            ...(offer === "follows" ? { follows: followsLine(step, steps) } : {}),
            ...(this.#busyShown && this.#pending.has(step.step_id) ? { pending: true } : {}),
            ...(offersSource(step, this.#accounts) ? { source: true } : {}),
          },
        };
      }),
      ...this.#removed.map((r) => ({ at: r.index, row: { kind: "removed" as const, stepId: r.stepId, what: r.what } })),
    ];
    rows.sort((a, b) => a.at - b.at);
    return {
      rows: rows.map((r) => r.row),
      hint: this.#last !== undefined ? "hidden" : this.#busyShown ? "busy" : "idle",
      ...(this.#error === undefined ? {} : { error: this.#error }),
      changed: this.#changed,
      ...(this.#last === undefined ? {} : { last: { what: this.#last.what } }),
      totalPending: this.#busyShown,
    };
  }

  #render(): void {
    if (this.#done) return;
    this.#d.card().editView(this.#view(), {
      onRemove: (id) => void this.remove(id),
      onAmount: (id) => this.amount(id),
      onSource: (id) => this.source(id),
      onUndo: (id) => void this.undo(id),
      onDone: () => void this.done(),
      onKeepAsWas: () => void this.keepAsWas(),
      onCancelLast: () => void this.cancelLast(),
      onKeepLast: () => this.keepLast(),
    });
  }

  // ---------------------------------------------------------------- one request at a time

  async #ask<T extends AmendResult | PreviewResult>(pending: readonly string[], call: () => Promise<T>): Promise<T | undefined> {
    if (this.#inFlight || this.#done) return undefined;
    this.#inFlight = true;
    this.#error = undefined;
    this.#pending = new Set(pending);
    this.#busyTimer = setTimeout(() => {
      this.#busyShown = true;
      this.#render();
      this.#d.card().announce("Updating");
    }, this.#d.busyAfterMs ?? 150);
    try {
      const answer = await call();
      // The card left the edit state while the bank answered (a new request retired it, it expired): the answer is dropped.
      return this.#done ? undefined : answer;
    } finally {
      if (this.#busyTimer !== undefined) clearTimeout(this.#busyTimer);
      this.#busyTimer = undefined;
      const shown = this.#busyShown;
      this.#busyShown = false;
      this.#pending = new Set();
      this.#inFlight = false;
      // The card says "Updating" no longer, whatever the answer (a refusal leaves the card as it was, not busy).
      if (shown) this.#render();
    }
  }

  /** What every failed answer does to the card (row 22x, §0.1's table); a refusal is the caller's. */
  #failed(r: Exclude<AmendResult, { readonly ok: true }>, retry: () => void): void {
    if (r.why === "stale") {
      if (r.latest !== undefined) this.#d.swap(r.latest);
      this.#error = { text: EDIT_WORDS.stale };
      this.#render();
      return;
    }
    if (r.why === "not-awaiting") {
      this.dispose();
      this.#d.ended();
      return;
    }
    if (r.why === "refused" && this.#stopped(r) !== undefined) {
      // H7: a Yes the bank carries — the card is stopped, not refused an edit: its paused end is the chat's to draw.
      this.dispose();
      this.#d.paused?.(this.#stopped(r) as RefusedStep, r.cancelled ?? []);
      return;
    }
    if (r.why === "refused") {
      // A refusal outside the amount sheet (an Undo that no longer fits, §2.8): the bank's sentence in the error line.
      this.#error = { text: refusalText(r.refused) };
      this.#render();
      return;
    }
    if (r.why === "would-empty") {
      // H5: what the customer removed would leave nothing — that is cancelling the card (22w), asked in the card.
      return;
    }
    this.#error = { text: EDIT_WORDS.unreachable, onRetry: retry };
    this.#render();
    this.#d.card().focus({ kind: "error" });
  }

  #applied(r: Extract<AmendResult, { readonly ok: true }>): void {
    this.#changed = true;
    // H4 (DCTA-171): a step the bank's new card has again is no longer "Removed" — a restored buy brings its move back.
    const now = new Set(r.bundle.signing_payload.steps.map((s) => s.step_id));
    this.#removed = this.#removed.filter((x) => !now.has(x.stepId));
    this.#d.swap(r.bundle);
  }

  // ---------------------------------------------------------------- remove, Undo, the last step

  async remove(stepId: string): Promise<void> {
    if (this.#inFlight || this.#done) return;
    const steps = this.#d.bundle().signing_payload.steps;
    const step = steps.find((s) => s.step_id === stepId);
    if (step === undefined) return;
    const what = whatOf(step, this.#flags());
    if (steps.length === 1) {
      this.#askLast(stepId, what);
      return;
    }
    const children = affectedBy(steps, stepId);
    if (children.length === 0) {
      await this.#remove(step, "keep");
      return;
    }
    const preview = await this.#ask(children.map((c) => c.step_id), () => this.#d.source.preview(this.#d.bundle(), { op: "remove", step_id: stepId, dependants: "remove" }));
    if (preview === undefined) return;
    if (!preview.ok) {
      if (preview.why === "would-empty") this.#askLast(stepId, what);
      else this.#failed(preview, () => void this.remove(stepId));
      return;
    }
    const share = children.find((c) => editKind(c) !== "follows");
    if (preview.wouldEmpty === true && share === undefined) {
      // H5: the buy and its move are all the card holds, and the move cannot stay — removing it is cancelling the card
      // (22w), never the sheet.
      this.#askLast(stepId, what);
      return;
    }
    // 22r: "Remove both" · "Keep {the order | the payment} at {amount}" (a share only — the bank's funding move goes with it) ·
    // "Cancel". H3: when the bank's move only changes (it pays for another buy too), nothing else goes: "Remove it" (R118).
    // H5: "Remove both" that would leave nothing is cancelling the card — 22w, then the cancel route; Keep stays a choice.
    const allGo = children.every((c) => preview.changes.some((ch) => ch.step_id === c.step_id && ch.change === "removed"));
    const removeBoth = preview.wouldEmpty === true ? (): Promise<void> => Promise.resolve(this.#askLast(stepId, what)) : (): Promise<void> => this.#remove(step, "remove");
    const choices: DependantsChoice[] = [
      { label: allGo || share !== undefined ? "Remove both" : "Remove it", kind: "btn", onSelect: () => void this.#closeSheet().then(removeBoth) },
      ...(share === undefined ? [] : [{ label: keepLabel(share), kind: "ghost" as const, onSelect: () => void this.#closeSheet().then(() => this.#remove(step, "keep")) }]),
      { label: "Cancel", kind: "link", onSelect: () => void this.#closeSheet().then(() => this.#d.card().focus({ kind: "remove", stepId })) },
    ];
    this.#open(dependantsSheet(`Remove ${what}?`, preview.words, choices), () => this.#d.card().focus({ kind: "remove", stepId }));
  }

  async #remove(step: SignedStep, dependants: "remove" | "keep"): Promise<void> {
    const before = this.#d.bundle();
    const steps = before.signing_payload.steps;
    const children = affectedBy(steps, step.step_id);
    const r = await this.#ask(children.map((c) => c.step_id), () =>
      this.#d.source.apply(before, { op: "remove", step_id: step.step_id, dependants }),
    );
    if (r === undefined) return;
    if (!r.ok) {
      if (r.why === "would-empty") this.#askLast(step.step_id, whatOf(step, this.#flags()));
      else this.#failed(r, () => void this.#remove(step, dependants));
      return;
    }
    const flags = before.firewall?.data_flags ?? [];
    const gone = steps.filter((s) => !r.bundle.signing_payload.steps.some((n) => n.step_id === s.step_id));
    this.#removed = [
      ...this.#removed,
      ...gone.map((s) => ({
        stepId: s.step_id,
        what: whatOf(s, flags),
        from: before.bundle_id,
        index: this.#order.get(s.step_id) ?? 0,
        ...(s.step_id === step.step_id ? {} : { along: true as const }),
      })),
    ];
    this.#applied(r);
    this.#render();
    const what = whatOf(step, flags);
    this.#d.card().announce(`Removed ${what}.`);
    this.#d.card().focus({ kind: "undo", stepId: step.step_id });
  }

  async undo(stepId: string): Promise<void> {
    const entry = this.#removed.find((r) => r.stepId === stepId);
    if (entry === undefined || this.#inFlight || this.#done) return;
    const r = await this.#ask([stepId], () => this.#d.source.apply(this.#d.bundle(), { op: "restore", step_id: stepId, from_bundle_id: entry.from }));
    if (r === undefined) return;
    if (!r.ok) {
      this.#failed(r, () => void this.undo(stepId));
      return;
    }
    this.#applied(r);
    this.#render();
    const back = r.bundle.signing_payload.steps.find((s) => s.step_id === stepId);
    this.#d.card().focus(back !== undefined && editKind(back) === "amount" ? { kind: "amount", stepId } : { kind: "remove", stepId });
  }

  /** 22w: removing what would leave nothing is cancelling the card — asked in the card, the rows' controls resting. */
  #askLast(stepId: string, what: string): void {
    this.#last = { stepId, what };
    this.#render();
    this.#d.card().focus({ kind: "keep-last" });
  }

  keepLast(): void {
    const last = this.#last;
    this.#last = undefined;
    this.#render();
    if (last !== undefined) this.#d.card().focus({ kind: "remove", stepId: last.stepId });
  }

  async cancelLast(): Promise<void> {
    if (this.#inFlight || this.#done || this.#last === undefined) return;
    this.#inFlight = true;
    const bundle = this.#d.bundle();
    const answer = await this.#d.cancel(bundle).finally(() => {
      this.#inFlight = false;
    });
    if (this.#done) return;
    if (answer === undefined) {
      this.#error = { text: EDIT_WORDS.unreachable, onRetry: () => void this.cancelLast() };
      this.#render();
      return;
    }
    this.dispose();
    const card = this.#d.card();
    card.editView(null);
    card.cancelled(answer.at);
    this.#d.left({ changed: this.#changed, removed: [], cancelled: true });
  }

  // ---------------------------------------------------------------- the amount

  amount(stepId: string): void {
    if (this.#inFlight || this.#done || this.#sheet !== undefined) return;
    const bundle = this.#d.bundle();
    const step = bundle.signing_payload.steps.find((s) => s.step_id === stepId);
    const m = step === undefined ? undefined : amountOf(step);
    if (step === undefined || m === undefined) return;
    const flags = this.#flags();
    const sheet = new AmountSheet(
      { who: whoLine(step, flags), previous: m.value, avail: availLine(step, bundle.signing_payload, flags) },
      {
        onChange: (value) => void this.#setAmount(step, value, "ask"),
        onCancel: () => void this.#closeSheet().then(() => this.#d.card().focus({ kind: "amount", stepId })),
        onLimits: this.#d.onLimits,
      },
    );
    this.#amountSheet = sheet;
    this.#open(sheet.element, () => this.#d.card().focus({ kind: "amount", stepId }));
    sheet.focusField();
  }

  /**
   * "Change to {amount}" (`ask`): a step others follow asks the bank first (22r); "Change both" (`follow`) sends the change
   * with its share following, as §0.1 says; "Keep the order at …" (`keep`) keeps the share at its figure (an ask to B46).
   */
  async #setAmount(step: SignedStep, value: string, how: "ask" | "follow" | "keep"): Promise<void> {
    const bundle = this.#d.bundle();
    if (amountOf(step)?.value === value && how === "ask") {
      // The same figure: nothing to change.
      await this.#closeSheet();
      this.#d.card().focus({ kind: "amount", stepId: step.step_id });
      return;
    }
    const children = affectedBy(bundle.signing_payload.steps, step.step_id);
    const edit: AmendEdit = { op: "set_amount", step_id: step.step_id, amount: { value, currency: "SGD" }, ...(how === "keep" ? { dependants: "keep" as const } : {}) };
    if (how === "ask" && children.length > 0) {
      this.#amountSheet?.busy(true);
      const preview = await this.#ask(children.map((c) => c.step_id), () => this.#d.source.preview(bundle, edit));
      this.#amountSheet?.busy(false);
      if (preview === undefined) return;
      if (!preview.ok) {
        if (preview.why === "refused" && this.#amountSheet !== undefined && this.#stopped(preview) === undefined) {
          this.#refused(preview.refused);
          return;
        }
        await this.#closeSheet();
        this.#failed(preview, () => this.amount(step.step_id));
        return;
      }
      const what = whatOf(step, this.#flags());
      const share = children.find((c) => editKind(c) !== "follows");
      const back = (): void => this.#d.card().focus({ kind: "amount", stepId: step.step_id });
      const choices: DependantsChoice[] = [
        { label: "Change both", kind: "btn", onSelect: () => void this.#closeSheet().then(() => this.#setAmount(step, value, "follow")) },
        ...(share === undefined ? [] : [{ label: keepLabel(share), kind: "ghost" as const, onSelect: () => void this.#closeSheet().then(() => this.#setAmount(step, value, "keep")) }]),
        { label: "Cancel", kind: "link", onSelect: () => void this.#closeSheet().then(back) },
      ];
      this.#open(dependantsSheet(`Change ${what} to ${formatMoney(value, "SGD")}?`, preview.words, choices), back);
      return;
    }
    this.#amountSheet?.busy(true);
    const r = await this.#ask([step.step_id, ...children.map((c) => c.step_id)], () => this.#d.source.apply(bundle, edit));
    if (r === undefined) {
      this.#amountSheet?.busy(false);
      return;
    }
    if (!r.ok) {
      // A refusal of the figure stays in the sheet (22p); a carried Yes stops the card itself (H7, #failed).
      if (r.why === "refused" && this.#amountSheet !== undefined && this.#stopped(r) === undefined) {
        this.#refused(r.refused);
        return;
      }
      await this.#closeSheet();
      this.#failed(r, () => void this.#setAmount(step, value, how));
      return;
    }
    await this.#closeSheet();
    this.#applied(r);
    this.#render();
    this.#d.card().announce(`Changed to ${formatMoney(value, "SGD")}.`);
    this.#d.card().focus({ kind: "amount", stepId: step.step_id });
  }

  // ---------------------------------------------------------------- the account (F39)

  /** "from {account}": the account sheet — the customer's own accounts with what each has; the one it comes from now pressed. */
  source(stepId: string): void {
    if (this.#inFlight || this.#done || this.#sheet !== undefined) return;
    const step = this.#d.bundle().signing_payload.steps.find((s) => s.step_id === stepId);
    if (step === undefined || (step.type !== "funds_transfer" && step.type !== "bill_payment")) return;
    const from = step.debit_account.account_id;
    const back = (): void => this.#d.card().focus({ kind: "source", stepId });
    const sheet = new AccountSheet(
      sourceChoices(step, this.#accounts).map((a) => ({
        accountId: a.account_id,
        label: accountLabel(a),
        available: formatMoney(a.available_balance, a.currency),
        current: a.account_id === from,
      })),
      {
        onPick: (accountId) => {
          if (accountId === from) void this.#closeSheet().then(back);
          else void this.#setSource(step, accountId);
        },
        onCancel: () => void this.#closeSheet().then(back),
      },
    );
    this.#accountSheet = sheet;
    this.#open(sheet.element, back);
    sheet.focusCurrent();
  }

  async #setSource(step: SignedStep, accountId: string): Promise<void> {
    this.#accountSheet?.busy(true);
    const r = await this.#ask([step.step_id], () => this.#d.source.apply(this.#d.bundle(), { op: "set_source", step_id: step.step_id, account_id: accountId }));
    if (r === undefined) {
      this.#accountSheet?.busy(false);
      return;
    }
    if (!r.ok) {
      if (r.why === "refused" && this.#accountSheet !== undefined) {
        this.#accountSheet.refused(refusalText(r.refused), accountId);
        return;
      }
      await this.#closeSheet();
      this.#failed(r, () => this.source(step.step_id));
      return;
    }
    await this.#closeSheet();
    this.#applied(r);
    this.#render();
    this.#d.card().focus({ kind: "source", stepId: step.step_id });
  }

  /** H7: the refusal is a Yes the bank carries AND the bank stopped this card — its paused step, else none. */
  #stopped(r: { readonly refused: readonly RefusedStep[]; readonly cancelled?: readonly string[] }): RefusedStep | undefined {
    return (r.cancelled ?? []).includes(this.#d.bundle().bundle_id) ? pausedRefusal(r) : undefined;
  }

  #refused(refused: readonly RefusedStep[]): void {
    const first = refused[0];
    const limits = first?.next_actions?.some((a) => a.action.kind === "bank_surface" && a.action.surface === "limits") === true;
    this.#amountSheet?.refused(refusalText(refused), limits && this.#d.onLimits !== undefined);
  }

  // ---------------------------------------------------------------- Done, and Keep it as it was

  async done(): Promise<void> {
    if (this.#inFlight || this.#done) return;
    await this.#closeSheet();
    this.dispose();
    const card = this.#d.card();
    card.editView(null);
    this.#d.left({ changed: this.#changed, removed: this.#removed, cancelled: false });
  }

  async keepAsWas(): Promise<void> {
    if (this.#inFlight || this.#done) return;
    const bundle = this.#d.bundle();
    const now = new Map(bundle.signing_payload.steps.map((s) => [s.step_id, s]));
    // §0.2: every removed step restored and every changed figure set back, in one apply.
    const edits: AmendEdit[] = [
      // Only what the customer removed: the bank brings back what went with it (H4) — restoring that too is no edit.
      ...this.#removed.filter((r) => r.along !== true).map((r) => ({ op: "restore" as const, step_id: r.stepId, from_bundle_id: r.from })),
      ...this.#base.signing_payload.steps.flatMap((was) => {
        const is = now.get(was.step_id);
        const before = amountOf(was);
        const after = is === undefined ? undefined : amountOf(is);
        if (before === undefined || after === undefined || before.value === after.value) return [];
        return [{ op: "set_amount" as const, step_id: was.step_id, amount: { value: before.value, currency: "SGD" as const }, dependants: "keep" as const }];
      }),
      // F39: every account changed put back.
      ...this.#base.signing_payload.steps.flatMap((was) => {
        const is = now.get(was.step_id);
        if (is === undefined || (was.type !== "funds_transfer" && was.type !== "bill_payment") || (is.type !== "funds_transfer" && is.type !== "bill_payment")) return [];
        return is.debit_account.account_id === was.debit_account.account_id ? [] : [{ op: "set_source" as const, step_id: was.step_id, account_id: was.debit_account.account_id }];
      }),
    ];
    if (edits.length === 0) {
      await this.done();
      return;
    }
    const r = await this.#ask(bundle.signing_payload.steps.map((s) => s.step_id), () => this.#d.source.apply(bundle, edits));
    if (r === undefined) return;
    if (!r.ok) {
      this.#failed(r, () => void this.keepAsWas());
      return;
    }
    this.#removed = [];
    this.#d.swap(r.bundle);
    this.dispose();
    this.#d.card().editView(null);
    this.#d.left({ changed: true, removed: [], cancelled: false });
  }

  // ---------------------------------------------------------------- sheets

  #open(sheet: HTMLElement, onDismissed: () => void): void {
    const show = (): void => {
      if (this.#done) return;
      this.#sheet = this.#d.openSheet(sheet, () => {
        void this.#closeSheet().then(onDismissed);
      });
    };
    // One sheet at a time: a sheet already up goes first (the amount sheet before the dependants sheet).
    if (this.#sheet === undefined) show();
    else void this.#closeSheet().then(show);
  }

  async #closeSheet(): Promise<void> {
    const sheet = this.#sheet;
    this.#sheet = undefined;
    this.#amountSheet = undefined;
    this.#accountSheet = undefined;
    await sheet?.close();
  }
}

/** "Keep the order at S$500.00" · "Keep the payment at S$500.00" (22r). */
function keepLabel(step: SignedStep): string {
  const m = amountOf(step);
  return `Keep ${step.type === "securities_order" ? "the order" : "the payment"} at ${m === undefined ? "" : formatMoney(m.value, m.currency)}`;
}

/** H7 (DCTA-171): the refusal is a Yes the bank carries, not a rule about the figure — the card is stopped. */
function pausedRefusal(r: { readonly refused: readonly RefusedStep[] }): RefusedStep | undefined {
  return r.refused.find((x) => x.reason_code === "SCAM_CHECK_PAUSED");
}

/** The bank's own words for a refused edit (22p), verbatim; one sentence, never a rule id. */
export function refusalText(refused: readonly RefusedStep[]): string {
  return refused[0]?.customer_message ?? "That amount can't be used here, so it wasn't changed.";
}

/** §0.2 `.op.erow.follows`: "Changes with the order for {share}" — the bank's funding move names the orders it pays for. */
function followsLine(step: SignedStep, steps: readonly SignedStep[]): string {
  const orders = steps.filter((s) => s.type === "securities_order" && ((s.depends_on ?? []).includes(step.step_id) || s.amount_derivation?.from_step === step.step_id));
  const names = orders.map((o) => (o.type === "securities_order" ? o.instrument.name : ""));
  if (names.length === 0) return "Changes with the order it pays for";
  return `Changes with the ${names.length === 1 ? "order" : "orders"} for ${names.join(" and ")}`;
}
